import React, { useState } from "react";
import {
  Box,
  Button,
  Popover,
  Typography,
  Stack,
  IconButton,
  Divider,
} from "@mui/material";
import { DateCalendar, TimePicker } from "@mui/x-date-pickers";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { CalendarToday, Close } from "@mui/icons-material";
import dayjs, { Dayjs } from "dayjs";
import { useTranslation } from "react-i18next";

interface DateTimeRangeFilterProps {
  onApply: (startDate: string | null, endDate: string | null) => void;
  onClear: () => void;
}

const DateTimeRangeFilter: React.FC<DateTimeRangeFilterProps> = ({
  onApply,
  onClear,
}) => {
  const { t } = useTranslation(["bugherd"]);
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [startDate, setStartDate] = useState<Dayjs | null>(null);
  const [endDate, setEndDate] = useState<Dayjs | null>(null);
  const [startTime, setStartTime] = useState<Dayjs | null>(null);
  const [endTime, setEndTime] = useState<Dayjs | null>(null);
  const [startMonth, setStartMonth] = useState<Dayjs>(dayjs());
  const [endMonth, setEndMonth] = useState<Dayjs>(dayjs().add(1, "month"));

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  // Handle start date selection (left calendar)
  const handleStartDateChange = (date: Dayjs | null) => {
    if (date) {
      setStartDate(date);
      // Clear end date when start date changes
      setEndDate(null);
      setEndTime(null);
      // Auto-advance end month to show dates after start date
      if (date.isSame(endMonth, "month") || date.isAfter(endMonth)) {
        setEndMonth(date.add(1, "month"));
      }
    }
  };

  // Handle end date selection (right calendar)
  const handleEndDateChange = (date: Dayjs | null) => {
    if (date) {
      setEndDate(date);
    }
  };

  const isInRange = (date: Dayjs) => {
    if (!startDate || !endDate) return false;
    return date.isAfter(startDate) && date.isBefore(endDate);
  };

  const isStartDate = (date: Dayjs) => {
    return startDate && date.isSame(startDate, "day");
  };

  const isEndDate = (date: Dayjs) => {
    return endDate && date.isSame(endDate, "day");
  };

  const handleApply = () => {
    let start: string | null = null;
    let end: string | null = null;

    if (startDate) {
      const dateWithTime = startTime
        ? startDate.hour(startTime.hour()).minute(startTime.minute()).second(0)
        : startDate.startOf("day");
      start = dateWithTime.toISOString();
    }

    if (endDate) {
      const dateWithTime = endTime
        ? endDate.hour(endTime.hour()).minute(endTime.minute()).second(59)
        : endDate.endOf("day");
      end = dateWithTime.toISOString();
    }

    onApply(start, end);
    handleClose();
  };

  const handleClearFilter = () => {
    setStartDate(null);
    setEndDate(null);
    setStartTime(null);
    setEndTime(null);
    onClear();
    handleClose();
  };

  const open = Boolean(anchorEl);

  const getDisplayText = () => {
    if (startDate && endDate) {
      return `${startDate.format("MMM D")} - ${endDate.format("MMM D, YYYY")}`;
    }
    if (startDate) {
      return `${startDate.format("MMM D, YYYY")} - ${t("selectEnd")}`;
    }
    return t("date");
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Button
        variant="outlined"
        size="small"
        onClick={handleClick}
        startIcon={<CalendarToday sx={{ fontSize: "16px" }} />}
        sx={{
          borderRadius: "6px",
          textTransform: "none",
          color: "text.primary",
          borderColor: startDate ? "primary.main" : "#ddd",
          backgroundColor: startDate ? "primary.lighter" : "transparent",
          "&:hover": {
            borderColor: "#aaa",
            backgroundColor: "background.paper",
          },
        }}
      >
        {getDisplayText()}
      </Button>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
      >
        <Box sx={{ p: 2 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 1,
            }}
          >
            <Typography variant="subtitle1" fontWeight={600}>
              {t("selectDateRange")}
            </Typography>
            <IconButton size="small" onClick={handleClose}>
              <Close fontSize="small" />
            </IconButton>
          </Box>

          {/* Two Calendar Side by Side */}
          <Stack
            direction="row"
            spacing={0}
            divider={<Divider orientation="vertical" flexItem />}
          >
            <Box>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ pl: 2, fontWeight: 600 }}
              >
                {t("Start Date")}
              </Typography>
              <DateCalendar
                value={startDate}
                onChange={handleStartDateChange}
                referenceDate={startMonth}
                onMonthChange={(month) => setStartMonth(month)}
                sx={{
                  "& .MuiPickersDay-root.Mui-selected": {
                    backgroundColor: "primary.main",
                  },
                }}
                slotProps={{
                  day: (ownerState) => ({
                    sx: {
                      ...(isInRange(ownerState.day) && {
                        backgroundColor: "primary.light",
                        color: "primary.contrastText",
                        "&:hover": {
                          backgroundColor: "primary.main",
                        },
                      }),
                      ...(isStartDate(ownerState.day) && {
                        backgroundColor: "primary.main",
                        color: "white",
                        borderRadius: "50%",
                      }),
                      ...(isEndDate(ownerState.day) && {
                        backgroundColor: "primary.main",
                        color: "white",
                        borderRadius: "50%",
                      }),
                    },
                  }),
                }}
              />
            </Box>
            <Box>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ pl: 2, fontWeight: 600 }}
              >
                {t("End Date")}
              </Typography>
              <DateCalendar
                value={endDate}
                onChange={handleEndDateChange}
                referenceDate={endMonth}
                onMonthChange={(month) => setEndMonth(month)}
                minDate={startDate ? startDate.add(1, "day") : undefined}
                disabled={!startDate}
                sx={{
                  "& .MuiPickersDay-root.Mui-selected": {
                    backgroundColor: "primary.main",
                  },
                  ...(!startDate && {
                    opacity: 0.5,
                    pointerEvents: "none",
                  }),
                }}
                slotProps={{
                  day: (ownerState) => ({
                    sx: {
                      ...(isInRange(ownerState.day) && {
                        backgroundColor: "primary.light",
                        color: "primary.contrastText",
                        "&:hover": {
                          backgroundColor: "primary.main",
                        },
                      }),
                      ...(isStartDate(ownerState.day) && {
                        backgroundColor: "primary.main",
                        color: "white",
                        borderRadius: "50%",
                      }),
                      ...(isEndDate(ownerState.day) && {
                        backgroundColor: "primary.main",
                        color: "white",
                        borderRadius: "50%",
                      }),
                    },
                  }),
                }}
              />
            </Box>
          </Stack>

          {/* Time Pickers */}
          <Stack direction="row" spacing={2} sx={{ mt: 2, px: 1 }}>
            <TimePicker
              label={t("startTime")}
              value={startTime}
              onChange={(newValue) => setStartTime(newValue)}
              slotProps={{
                textField: { size: "small", fullWidth: true },
              }}
            />
            <TimePicker
              label={t("endTime")}
              value={endTime}
              onChange={(newValue) => setEndTime(newValue)}
              slotProps={{
                textField: { size: "small", fullWidth: true },
              }}
            />
          </Stack>

          {/* Selected Range Display */}
          {(startDate || endDate) && (
            <Box sx={{ mt: 2, px: 1 }}>
              <Typography variant="body2" color="text.secondary">
                {t("selectedColon")} {startDate?.format("MMM D, YYYY") || "..."} →{" "}
                {endDate?.format("MMM D, YYYY") || "..."}
              </Typography>
            </Box>
          )}

          {/* Action Buttons */}
          <Stack
            direction="row"
            spacing={2}
            justifyContent="flex-end"
            sx={{ mt: 2 }}
          >
            <Button variant="outlined" size="small" onClick={handleClearFilter}>
              {t("clear")}
            </Button>
            <Button
              variant="contained"
              size="small"
              onClick={handleApply}
              disabled={!startDate}
            >
              {t("apply")}
            </Button>
          </Stack>
        </Box>
      </Popover>
    </LocalizationProvider>
  );
};

export default DateTimeRangeFilter;
